import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

export default function OAuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  // Prevent duplicate processing with a ref
  const processingComplete = useRef(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL 
  const frontendRedirectUriForTokenExchange = process.env.REACT_APP_REDIRECT_URI;

  useEffect(() => {
    // Prevent multiple executions of the auth callback logic
    if (processingComplete.current) return;

    async function handleOAuthCallback() {
      try {
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get("code");
        const returnedState = urlParams.get("state");
        const oauthError = urlParams.get("error");

        // Log the full URL (without the code) for debugging
        console.log("", 
          location.pathname + "?" + Array.from(urlParams.entries())
            .filter(([key]) => key !== "code")
            .map(([key, value]) => `${key}=${value}`)
            .join("&")
        );

        if (oauthError) {
          console.error("OAuth error from Google:", oauthError);
          setError(`Authentication error from Google: ${oauthError}. Redirecting to sign up...`);
          setTimeout(() => navigate("/signup"), 5000);
          return;
        }

        // Get state from localStorage but don't remove it yet
        const savedState = localStorage.getItem("oauthState") || sessionStorage.getItem("oauthState");
        
        // console.log("OAuth State Check - Returned state:", returnedState);
        // console.log("OAuth State Check - Saved state:", savedState);
        
        if (!returnedState || !savedState || returnedState !== savedState) {
          console.error("Invalid state parameter. Returned:", returnedState, "Saved:", savedState);
          setError("Invalid authentication state (CSRF potential). Please try signing up again.");
          setTimeout(() => navigate("/signup"), 5000);
          return;
        }
        
        // Only AFTER validating the state, remove it from storage
        // This prevents the race condition where state is removed before validation completes
        processingComplete.current = true; // Mark processing as complete to prevent reruns
        localStorage.removeItem("oauthState");
        sessionStorage.removeItem("oauthState");

        if (!code) {
          setError("No authorization code received from Google. Please try again.");
          setTimeout(() => navigate("/signup"), 5000);
          return;
        }

        if (!frontendRedirectUriForTokenExchange) {
          setError("Frontend redirect URI for token exchange is not configured (REACT_APP_GOOGLE_REDIRECT_URI).");
          setLoading(false);
          return;
        }

        // Log redacted code for debugging
        // console.log("OAuthCallback: Authorization code received:", code.substring(0, 10) + "...[redacted]");
        // console.log("OAuthCallback: Sending code to backend with redirect_uri:", frontendRedirectUriForTokenExchange);

        const response = await fetch(`${backendUrl}/auth/google/callback/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            code,
            redirect_uri: frontendRedirectUriForTokenExchange,
          }),
          signal: AbortSignal.timeout(15000), // 15-second timeout
        });

        if (!response.ok) {
          const errorText = await response.text();
          let detailedErrorMessage = "Authentication failed on the backend.";
          try {
            const errorData = JSON.parse(errorText);
            detailedErrorMessage = errorData.error || errorData.detail || `Backend error: ${response.statusText}`;
          } catch (e) {
            detailedErrorMessage = errorText.length < 200 && errorText.length > 5 ? errorText : `Backend request failed with status ${response.status}. Check backend logs.`;
            console.error("Failed to parse backend error response or response was not JSON:", errorText);
          }
          throw new Error(detailedErrorMessage);
        }

        const data = await response.json();
        // console.log("OAuthCallback: Successfully processed backend response");
        
        // Store auth data
        localStorage.setItem("authToken", data.token || data.access_token);
        if (data.refresh_token) localStorage.setItem("refreshToken", data.refresh_token);
        localStorage.setItem("userId", data.user_id);

        // Navigate based on profile completion status
        if (data.is_profile_complete) {
          navigate("/");
        } else {
          navigate("/user-details", { state: { newUser: true } });
        }
      } catch (err) {
        console.error("OAuth callback processing error:", err);
        setError(err.message || "Authentication failed during callback. Redirecting to sign up...");
        setTimeout(() => navigate("/signup"), 7000); // Longer timeout to read error
      } finally {
        setLoading(false);
      }
    }

    handleOAuthCallback();
  }, [location, navigate, backendUrl, frontendRedirectUriForTokenExchange]); // Add dependencies

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {error ? "Authentication Error" : "Completing Authentication"}
        </h2>
        {loading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="h-10 w-10 animate-spin text-[#D4AF37] mb-4" />
            <p className="text-gray-600">Processing your sign in with Google...</p>
          </div>
        ) : error ? (
          <div>
            <p className="text-red-600 mb-4 whitespace-pre-wrap">{error}</p>
            <p className="text-gray-600">You will be redirected to the sign up page shortly.</p>
          </div>
        ) : (
          <div>
            <p className="text-green-600 mb-4">Authentication successful!</p>
            <p className="text-gray-600">Redirecting you...</p>
          </div>
        )}
      </div>
    </div>
  );
}
